import FormImageInput from "@/components/forms/FormImageInput";
import FormInput from "@/components/forms/FormInput";
import FormSelect from "@/components/forms/FormSelect";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { useCreatePopularSearchMutation } from "@/app/features/popularSearch/popularSearchApiSlice";
import FormTextArea from "@/components/forms/FormTextArea";

const PopularSearchesTypeEnum = {
  LIFESTYLE: "Lifestyle",
  CATEGORY: "Category",
};

const popularSearchTypes = [
  {
    code: PopularSearchesTypeEnum.CATEGORY,
    name: PopularSearchesTypeEnum.CATEGORY,
  },
  {
    code: PopularSearchesTypeEnum.LIFESTYLE,
    name: PopularSearchesTypeEnum.LIFESTYLE,
  },
];

const initialValues = {
  name: "",
  description: "",
  type: PopularSearchesTypeEnum.CATEGORY,
  car_1: "",
  car_2: "",
  car_3: "",
  car_4: "",
  car_5: "",
  car_6: "",
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(2).max(50).label("Name").trim(),
  description: Yup.string()
    .required()
    .min(5)
    .max(500)
    .label("Description")
    .trim(),
  type: Yup.string()
    .required()
    .oneOf(["Category", "Lifestyle"], "Type must be Category or Lifestyle")
    .label("Type")
    .trim(),

  car_1: Yup.string().required().min(1).label("Car 1").trim(),
  car_2: Yup.string().required().min(1).label("Car 2").trim(),
  car_3: Yup.string().required().min(1).label("Car 3").trim(),
  car_4: Yup.string().required().min(1).label("Car 4").trim(),
  car_5: Yup.string().required().min(1).label("Car 5").trim(),
  car_6: Yup.string().required().min(1).label("Car 6").trim(),

  image: Yup.mixed()
    .nullable(true)
    .when("type", {
      is: "Category",
      then: (schema) =>
        schema
          .required("Image is required for Category")
          .test(
            "fileType",
            "Only the following formats are accepted: .jpg, .jpeg, .png, .webp",
            (value) =>
              !value ||
              (value &&
                ["image/png", "image/jpg", "image/jpeg", "image/webp"].includes(
                  value.type
                ))
          ),
      otherwise: (schema) => schema.notRequired(),
    })
    .label("Image"),
});

export default function CreatePopularSearchPage() {
  const [createPost, { isLoading }] = useCreatePopularSearchMutation();

  const navigate = useNavigate();

  const handleSubmit = async (inputs) => {
    try {
      const formData = new FormData();
      formData.append("name", inputs.name);
      formData.append("description", inputs.description);
      formData.append("type", inputs.type);
      formData.append("car_1", inputs.car_1);
      formData.append("car_2", inputs.car_2);
      formData.append("car_3", inputs.car_3);
      formData.append("car_4", inputs.car_4);
      formData.append("car_5", inputs.car_5);
      formData.append("car_6", inputs.car_6);

      if (inputs.image && inputs.type === "Category") {
        formData.append("image", inputs.image); // new image
      }

      const result = await createPost(formData);

      if (result && result.data) {
        toast.success(result.data.message);
        navigate("/popular-search");
      } else if (result && result.error) {
        toast.error(result.error.data.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Create Popular Search</Typography>
          </Stack>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({
              setFieldValue,
              values,
              setFieldTouched,
              setFieldError,
              errors,
              resetForm,
            }) => (
              <Form>
                <Stack spacing={3}>
                  <FormInput
                    label="Name"
                    name="name"
                    type="text"
                    placeholder="Enter Name"
                  />
                  <FormSelect
                    name="type"
                    label="Select Type"
                    items={popularSearchTypes}
                  />
                  <FormTextArea
                    label="Description"
                    name="description"
                    type="text"
                    placeholder="Enter Description"
                  />
                  <FormInput
                    label="Car 1"
                    name="car_1"
                    type="text"
                    placeholder="Enter Car 1"
                  />
                  <FormInput
                    label="Car 2"
                    name="car_2"
                    type="text"
                    placeholder="Enter Car 2"
                  />
                  <FormInput
                    label="Car 3"
                    name="car_3"
                    type="text"
                    placeholder="Enter Car 3"
                  />
                  <FormInput
                    label="Car 4"
                    name="car_4"
                    type="text"
                    placeholder="Enter Car 4"
                  />
                  <FormInput
                    label="Car 5"
                    name="car_5"
                    type="text"
                    placeholder="Enter Car 5"
                  />
                  <FormInput
                    label="Car 6"
                    name="car_6"
                    type="text"
                    placeholder="Enter Car 6"
                  />
                  {values.type === PopularSearchesTypeEnum.CATEGORY && (
                    <FormImageInput
                      name="image"
                      imageValues={values.image}
                      onChangeSelectedImage={(event) => {
                        setFieldTouched(event.target.name);
                        setFieldValue(event.target.name, event.target.files[0]);
                      }}
                      onError={(message) => {
                        setFieldError("image", message);
                      }}
                      onCheckRes={(checkResolution) => {
                        setFieldValue("checkResolution", checkResolution);
                      }}
                      checkResolution={values.checkResolution}
                      label="Image"
                    />
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                  >
                    Submit
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}
